import type {ReactElement} from 'react';
import {useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {InputField} from '@Components/input-field-v2';
import type {IconsOnlyControl, InputFieldControls} from '@Components/input-field-v2/input-field.types';
import {InputControlItemType, InputFieldSize, InputFieldState, InputFieldType} from '@Components/input-field-v2/input-field.types';
import useOutsideClick from '@Hooks/useOutsideClick';
import {useAppDispatch} from '@/hooks';
import {EventWizardTestId} from '@/test-ids/event-wizard-test-id';
import {onEventWizardRegistrationChange} from '@Components/event-wizard/event-wizard-slice';
import {useEventWizardRegistrationDetails} from '@Components/event-wizard/event-wizard.hooks';
import {toggleDisplayPreviewRegistrationForm} from '@Components/event-wizard/preview/preview.helper';
import {isValidURL2} from '@Utils/url.util';
import type {EventWizardRegistrationsButtonLinkProps} from '@Components/event-wizard/event-wizard-registrations-button-link/event-wizard-registrations-button-link.types';

export function EventWizardRegistrationsButtonLink({onAddRegistrationLink}: EventWizardRegistrationsButtonLinkProps): ReactElement<any> {
  const dispatch = useAppDispatch();
  const registrationDetails = useEventWizardRegistrationDetails();

  const [isCustomLinkOptionOpen, setCustomLinkOptionOpen] = useState<boolean>(!!registrationDetails.registerBtnLink);
  const [buttonLink, setButtonLink] = useState<string>(registrationDetails.registerBtnLink ?? '');
  const [isLinkAdded, setIsLinkAdded] = useState<boolean>(!!registrationDetails.registerBtnLink);
  const [isInErrState, setIsInErrState] = useState<boolean>(!!registrationDetails.registerBtnLink && !isValidURL2(registrationDetails.registerBtnLink));

  const inputFieldRef = useRef<HTMLInputElement>(null);
  const selectorContainerRef = useRef<HTMLDivElement>(null);

  useOutsideClick(
    inputFieldRef,
    () => {
      if (buttonLink === '') {
        updateCustomLinkOptionVisibility(false);
      }
    },
    selectorContainerRef?.current
  );

  const updateCustomLinkOptionVisibility = (shouldDisplay: boolean): void => {
    setCustomLinkOptionOpen(shouldDisplay);
    toggleDisplayPreviewRegistrationForm(!shouldDisplay);

    if (!shouldDisplay) {
      saveLink('');
      setIsInErrState(false);
    }
  };

  const onDeleteLink = (): void => {
    setButtonLink('');
    updateCustomLinkOptionVisibility(false);
    setIsLinkAdded(false);
    saveLink('');
  };

  const onUpdateLink = (): void => {
    saveLink(buttonLink);
    setIsLinkAdded(buttonLink !== '' && isValidURL2(buttonLink));
    setIsInErrState(!isValidURL2(buttonLink));
  };

  const saveLink = (url: string): void => {
    const saveUrl = !url ? undefined : url;
    dispatch(
      onEventWizardRegistrationChange({
        ...registrationDetails,
        registerBtnLink: saveUrl,
      })
    );
    onAddRegistrationLink(saveUrl);
  };

  const onTypeLink = (text: string): void => {
    setIsInErrState(false);
    setButtonLink(text);

    if (text === '' || text !== registrationDetails.registerBtnLink) {
      setIsLinkAdded(false);
      return;
    }

    if (text === registrationDetails.registerBtnLink) {
      setIsLinkAdded(true);
    }
  };

  const getDefaultView = (): ReactElement<any> => {
    return (
      <ClickableDiv
        ref={selectorContainerRef}
        data-testid={EventWizardTestId.ADD_CUSTOM_LINK_BUTTON}
        onClick={() => {
          updateCustomLinkOptionVisibility(true);
        }}
        className="flex-row-align-center _cursor-pointer"
      >
        <Icon icon="icon-link" size={IconSize.SIZE_ICON_16} className="content-primary spacing-m-r-1" />
        <Text className="content-primary" val={window.i18next.t('pmwjs_add_custom_link_to_button')} size={TextSize.XXSMALL} bold />
      </ClickableDiv>
    );
  };

  const getLinkInputView = (): ReactElement<any> => {
    return (
      <div className="flex-row-align-center align-items-start justify-content-center">
        <div className="flex-row-align-center spacing-m-t-2 spacing-m-r-10 _text-nowrap">
          <Icon icon="icon-link" size={IconSize.SIZE_ICON_16} className="spacing-m-r-1" />
          <Text val={window.i18next.t('pmwjs_button_link')} size={TextSize.XXSMALL} bold />
        </div>
        <div className="_full-width">
          <InputField
            ref={inputFieldRef}
            inputTestId={EventWizardTestId.CUSTOM_LINK_INPUT}
            input={buttonLink}
            inputType={InputFieldType.TEXT}
            onInputChange={onTypeLink}
            onInputBlur={onUpdateLink}
            size={InputFieldSize.XSMALL}
            state={isInErrState ? InputFieldState.ERROR : InputFieldState.DEFAULT}
            information={isInErrState ? window.i18next.t('pmwjs_enter_a_valid_link') : undefined}
            placeholder={window.i18next.t('pmwjs_add_custom_link_to_button')}
            controls={isLinkAdded ? getInputControls() : undefined}
            onInputSubmit={onUpdateLink}
          />
        </div>
      </div>
    );
  };

  const getInputControls = (): InputFieldControls => {
    const controlIcon: IconsOnlyControl = {
      type: InputControlItemType.ICONS_ONLY,
      id: 'icon-delete',
      icon: 'icon-delete',
      className: 'content-danger',
    };
    return {
      showDefaultControls: false,
      customControls: [controlIcon],
      onClick: onDeleteLink,
    };
  };

  return isCustomLinkOptionOpen ? getLinkInputView() : getDefaultView();
}
